import { FC, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { mixed, object, string } from 'yup';

import {
  Button,
  Form,
  Modal,
  NotificationVariants,
  ProcessLoader,
  Radio,
  RadioGroup,
  RadioGroupDirection,
  TextArea,
} from '@common/components';
import { Sizes, Variants } from '@common/types';
import { COMMON_TRANSLATION, PATHS } from '@/common/constants';
import { useAddNotification } from '@modules/notifications/store';

import {
  CancelAppointmentFormValues,
  CancelAppointmentModalProps,
  CancelAppointmentReason,
} from './CancelAppointmentModal.types';
import { GENETIC_COUNSELING_TRANSLATION, PATIENT_PROFILE_QUERY_KEY } from '../../constants';
import useMutationCancelAppointment from '../../hooks/useMutationCancelAppointment';
import { StyledActionsContainer, StyledProccessLoaderContainer } from './CancelAppointmentModal.styles';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CancelAppointmentFormValuesVariables } from '../../types';

const schema = object({
  reason: mixed().oneOf(Object.values(CancelAppointmentReason)).required(),
  otherReason: string().optional(),
});

const CancelAppointmentModal: FC<CancelAppointmentModalProps> = ({ id, isOpen, onClose }) => {
  const navigate = useNavigate();
  const { t } = useTranslation(GENETIC_COUNSELING_TRANSLATION);
  const [reason, setReason] = useState<CancelAppointmentReason>();
  const queryClient = useQueryClient();

  const { mutateAsync, isSuccess, isLoading } = useMutationCancelAppointment(
    onCancelAppointmentSuccess,
    onCancelAppointmentError,
  );
  const addNotification = useAddNotification();

  const {
    handleSubmit,
    control,
    formState: { isValid },
    setValue,
  } = useForm<CancelAppointmentFormValues>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  const handleSubmitButton = async (data: CancelAppointmentFormValues): Promise<void> => {
    try {
      await mutateAsync({ id, reasonDescription: t(`cancel_appointment.reason.${data.reason}`), ...data });
    } catch (error: unknown) {}
  };

  function onCancelAppointmentSuccess(
    appointmentID: string | undefined,
    variables: CancelAppointmentFormValuesVariables,
  ): void {
    const redirectToReferrals: boolean =
      variables.reason === CancelAppointmentReason.RESCHEDULE ||
      variables.reason === CancelAppointmentReason.NOT_INTERESTED;
    setTimeout(() => {
      onClose();
      !redirectToReferrals && queryClient.invalidateQueries([PATIENT_PROFILE_QUERY_KEY]).catch(() => {});

      const description = redirectToReferrals
        ? t('cancel_appointment.notification.success_referrals_description')
        : t('cancel_appointment.notification.success_declined_description');
      addNotification({
        title: t('cancel_appointment.notification.success_title'),
        description,
        type: NotificationVariants.INFO,
        autoClose: true,
      });

      redirectToReferrals &&
        appointmentID &&
        navigate(`/${PATHS.GENETIC_COUNSELING.ROOT}/${PATHS.GENETIC_COUNSELING.PATIENT}/${appointmentID}`);
    }, 3000);
  }

  function onCancelAppointmentError(): void {
    addNotification({
      title: t('cancel_appointment.notification.error_title'),
      type: NotificationVariants.ERROR,
      autoClose: true,
    });
  }

  useEffect(() => {
    // Clean "Other" reason text if option is different than Other.
    if (reason !== CancelAppointmentReason.OTHER) {
      setValue('otherReason', undefined);
    }
  }, [reason]);

  return (
    <Modal
      show={isOpen}
      isLocked={isSuccess || isLoading}
      size={Sizes.SMALL}
      title={t('cancel_appointment.title')}
      titleColor={'$primary'}
      onClose={onClose}
    >
      {!isSuccess && (
        <Form onSubmit={handleSubmit(handleSubmitButton)}>
          {/* Reason */}
          <Controller
            control={control}
            name="reason"
            render={({ field: { onChange } }) => {
              return (
                <RadioGroup
                  name="reason"
                  id="reason"
                  direction={RadioGroupDirection.COLUMN}
                  onChange={(e) => {
                    onChange(e);
                    setReason(e?.target?.value as CancelAppointmentReason);
                  }}
                >
                  {Object.values(CancelAppointmentReason).map((reason) => {
                    return (
                      <Radio key={reason} id={reason} value={reason}>
                        {t(`cancel_appointment.reason.${reason}`)}
                      </Radio>
                    );
                  })}
                </RadioGroup>
              );
            }}
          />
          {/* Other Reason text */}
          {reason === CancelAppointmentReason.OTHER && (
            <Controller
              control={control}
              name="otherReason"
              render={({ field: { onChange: onChangeReasonText, ...props } }) => (
                <TextArea
                  {...props}
                  id="cancel-appt-other-reason"
                  name="otherReason"
                  onChange={(e) => onChangeReasonText(e)}
                  placeholder={t('optional', { ns: COMMON_TRANSLATION })}
                />
              )}
            />
          )}
          {/* Actions */}
          <StyledActionsContainer>
            <Button disabled={!isValid} type="submit" loading={isLoading} id="cancel-appointment-submit">
              {t('cancel_appointment.confirm')}
            </Button>
            <Button type="button" variant={Variants.TERTIARY} onClick={onClose} id="cancel-appointment-cancel">
              {t('cancel_appointment.cancel')}
            </Button>
          </StyledActionsContainer>
        </Form>
      )}
      {isSuccess && (
        <StyledProccessLoaderContainer>
          <ProcessLoader
            description={t('cancel_appointment.success_loading.description')}
            title={t('cancel_appointment.success_loading.title')}
          />
        </StyledProccessLoaderContainer>
      )}
    </Modal>
  );
};

export default CancelAppointmentModal;
